import React, { useEffect, useState } from 'react';
import { Field, Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Button, Label, TextArea } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Row } from 'reactstrap';
import { CaseArchiveModel } from 'app/edozvola/cases/cases.model';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCaseArchive } from 'app/edozvola/cases/service/cases.service';
import { APP_DATE_FORMAT, ERRORS, messages } from 'app/config/constants';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { fetchRetentionPeriodCodebook } from 'app/edozvola/codebooks/codebooks.service';
import { CodebookModel } from 'app/shared/model/shared.model';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { updateCaseArchiveMutation } from 'app/edozvola/cases/service/cases.queries';

const CaseArchiveForm = () => {
  const { id } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<CaseArchiveModel>({} as CaseArchiveModel);
  const [retentionPeriodCodebookOptions, setRetentionPeriodCodebookOptions] = useState<CodebookModel[]>([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [readonly, setReadonly] = useState(true);
  const {mutate: saveMutation, isPending: isSaving} = updateCaseArchiveMutation(id, (data) => {
    setInitialData(data);
    showToast(toast.success, messages.DATA_SAVED);
  }, (error) => {
    if (Object.values(ERRORS).includes(error.response?.data?.detail)) {
      showToast(toast.error, error.response?.data?.detail);
    } else {
      showToast(toast.error, ERRORS.GENERIC_ERROR);
    }
  });

  useEffect(() => {
    fetchCodebooks().then(() => {
      fetchCaseArchive(id)
        .then(data => {
          setReadonly(data.readonly);
          setInitialData(data);
        })
        .catch(error => {
          if (Object.values(ERRORS).includes(error.response?.data?.detail)) {
            showToast(toast.error, error.response?.data?.detail);
          } else {
            showToast(toast.error, ERRORS.GENERIC_ERROR);
          }
          navigate('/cases/');
        });
    });
  }, [id]);

  const fetchCodebooks = async () => {
    await fetchRetentionPeriodCodebook().then(data => setRetentionPeriodCodebookOptions(data));
  };

  const FormikValueObserver = ({ onChange }) => {
    const { dirty } = useFormikContext();

    useEffect(() => onChange({ dirty }), [dirty, onChange]);

    return null;
  };

  const validation = Yup.object({
    retentionPeriod: Yup.object().required(messages.MANDATORY_FIELD),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    saveMutation(values);
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialData}
        validationSchema={validation}
        validateOnMount={true}
        onSubmit={handleSubmit}
      >
        {({ errors, isValid, dirty, submitForm, setFieldValue }) => (
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            <h5 className={'form-header'}>Arhiviranje</h5>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Period čuvanja'} />
                  <Field
                    id="retentionPeriod"
                    name="retentionPeriod"
                    render={({ field }) => (
                      <PaginatedDropdown
                        placeholder={!readonly && 'Odaberi period čuvanja'}
                        onItemSelect={selectedOption => setFieldValue('retentionPeriod', selectedOption[0])}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={retentionPeriodCodebookOptions}
                        disabled
                        error={!readonly && (errors.retentionPeriod as string)}
                        showErrorMessage
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Napomena za arhivu'} />
                  <Field
                    id="archiveComment"
                    name="archiveComment"
                    render={({ field }) => (
                      <TextArea {...field} type="input" error={!readonly && errors.archiveComment} disabled={readonly} />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Datum arhiviranja'} />
                  <Field
                    id="archiveDate"
                    name="archiveDate"
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={setFieldValue} dateFormat={APP_DATE_FORMAT} disabled={true} />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>

            {!readonly && (
              <div className="case-save-button">
                <Button
                  color={'info'}
                  label={'Spremi'}
                  size={'lg'}
                  isLoading={isSaving}
                  onClick={submitForm}
                  disabled={!dirty || !isValid}
                />
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CaseArchiveForm;
